import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useHistory } from "react-router-dom";
import useQueryFetch from "../../modules/api/apiHooks/useQueryFetch";
import { getAtkHeader } from "../../modules/api/fetch/getHeaders";
import { useAuth, usePlayPermissionCheck } from "../../modules/auth/authHooks";
import { ResponsivePresetKeys, ResponsivePresets } from "../../modules/configs/responsiveImageConfigs";
import { GET_USER_ONLINE_SCREENING } from "../../modules/graphql/accountGraphQL";
import { EpisodeAccessType, InterestAction } from "../../modules/graphql/api.generated";
import { TOGGLE_BOOKMARK_PROGRAM } from "../../modules/graphql/bookmarkGraphQL";
import { ADD_INTEREST } from "../../modules/graphql/programGraphQL";
import clsx from "../../modules/helpers/clsx";
import LocalStorageService from "../../modules/helpers/localStorageService";
import toReadableTimeFormat from "../../modules/player/utils/toReadableTimeFormat";
import { getPlayButtonData } from "../../modules/shared/heroSection/helpers";
import { useHeroSectionSelector } from "../../modules/shared/heroSection/heroSectionContext/heroSectionContextHooks";
import { setFocus } from "../../modules/spatialNavigation";
import { ToastActionType } from "../../modules/toast/context/toast.interface";
import { useToast } from "../../modules/toast/context/toastContextHooks";
import TextDialog from "../shared/dialogs/textDialog/TextDialog";
import FocusableButton from "../shared/focusableButton/FocusableButton";
import IconComponent from "../shared/icon/IconComponent";
import IconButton from "../shared/iconButton/IconButtonComponent";
import ImageGenerator from "../shared/imageGenerator/ImageGenerator";
import TagItem from "../shared/tagButton/TagButton";
import * as styles from "./styles.module.scss"; // Adjust the path as necessary

const HeroSection = () => {
  const heroSectionData = useHeroSectionSelector();
  const [getUserOnlineScreening, userOnlineScreening] = useQueryFetch(
    process.env.TV_ACCOUNT_URL!,
    GET_USER_ONLINE_SCREENING,
    getAtkHeader,
  );
  const playPermission = usePlayPermissionCheck(
    heroSectionData.episode,
    null,
    heroSectionData.programStatusCode,
    userOnlineScreening,
  );

  const [osRemainingTime, setOsRemainingTime] = useState<number | null>(null);
  const remainingTimeIntervalRef = useRef<ReturnType<typeof setInterval>>(null);
  const auth = useAuth();

  useEffect(() => {
    if (auth.user && heroSectionData.episode?.accessType === EpisodeAccessType.OnlineScreening) {
      getUserOnlineScreening({
        programId: heroSectionData.programId,
      }).then((res) => {
        if (remainingTimeIntervalRef.current) {
          clearInterval(remainingTimeIntervalRef.current);
        }
        if (res.data?.getUserScreening?.data) {
          const diff = new Date(res.data.getUserScreening.data.endingDate).getTime() - new Date().getTime();
          setOsRemainingTime(Math.floor(diff / 1000));
          if (diff > 0) {
            remainingTimeIntervalRef.current = setInterval(() => {
              setOsRemainingTime((prev) => prev! - 1);
              if (typeof osRemainingTime === "number" && osRemainingTime < 1) {
                clearInterval(remainingTimeIntervalRef.current!);
              }
            }, 1000);
          }
        }
      });
    }
  }, [heroSectionData.episode]);

  const toast = useToast();
  const [favorites, setFavorites] = useState<{ interest: InterestAction; isBookmarked: boolean } | null>(null);
  const playButtonData = getPlayButtonData(
    playPermission?.hasPermission,
    playPermission?.reason!,
    heroSectionData.episode?.uid,
    heroSectionData.programUid,
    playPermission?.ispLabel,
  );

  const history = useHistory();
  const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);
  useEffect(() => {
    setFavorites((prev: any) => ({
      ...prev,
      interest: prev && prev.interest ? prev.interest : heroSectionData?.interestDetails?.interestedByUser,
      isBookmarked: prev && prev.isBookmarked != null ? prev.isBookmarked : heroSectionData.isBookMarkedByMe,
    }));
  }, [heroSectionData.isBookMarkedByMe]);

  const likes = useMemo(() => {
    let percentage = 0;
    if (heroSectionData.statisticDetails) {
      percentage =
        heroSectionData.statisticDetails.totalLikePercentage + heroSectionData.statisticDetails.totalLovePercentage;
    }
    return percentage;
  }, [heroSectionData.statisticDetails]);

  const [toggleBookmark, toggleBookmarkContext] = useQueryFetch(
    process.env.TV_API_BASE_URL!,
    TOGGLE_BOOKMARK_PROGRAM,
    getAtkHeader,
  );
  const [addInterest] = useQueryFetch(process.env.TV_API_BASE_URL!, ADD_INTEREST, getAtkHeader);
  const onClickingBookmark = useCallback(async () => {
    const data = await toggleBookmark({ programID: heroSectionData.programId });
    toast({
      message: data?.data?.toggleBookmarkProgram?.status?.message,
      options: { type: ToastActionType.SUCCESS, duration: 2000 },
    });

    setFavorites((prev: any) => ({
      ...prev,
      interest: prev && prev.interest ? prev.interest : null,
      isBookmarked: !!data.data.toggleBookmarkProgram.data,
    }));
  }, [heroSectionData.programId]);

  const onClickingInterestButton = useCallback(
    async (type: InterestAction) => {
      const data = await addInterest({ intType: "PROGRAM", itemID: heroSectionData.programId, action: type });
      toast({
        message: data?.data?.addInterest?.status?.message,
        options: { type: ToastActionType.SUCCESS, duration: 2000 },
      });
      setFavorites((prev: any) => ({
        ...prev,
        interest: data.data?.addInterest?.data?.action,
        isBookmarked: prev ? prev.isBookmarked : null,
      }));
    },
    [heroSectionData.programId],
  );

  const handleCloseDescriptionDialog = useCallback(() => {
    setShowDescriptionDialog(false);
    setFocus("readMoreButton");
  }, []);

  return (
    <>
      <div className={styles.heroSectionContainer}>
        <div className={styles.bannerContainer}>
          <ImageGenerator
            noBlurPlaceholder={true}
            imageType="landscape"
            sizes={ResponsivePresets[ResponsivePresetKeys.BILLBOARD]}
            src={heroSectionData.imagePath}
          />
          {heroSectionData && heroSectionData.programStatusCode && heroSectionData.programStatusCode === 451 && (
            <div className={styles.copyRightLayer}>
              <div className={styles.statusCode}>451 ERROR</div>
              <div className={styles.errorDetail}>Unavailable for legal reasons</div>
            </div>
          )}

          <div className={styles.bannerGradientImage} />
        </div>

        {heroSectionData.isCampaign && (
          <div className={styles.campaignBox}>
            <QRCode
              className={styles.campaignQrCode}
              fgColor="#8b5cf6"
              level="Q"
              size={180}
              value={heroSectionData.campaignUrl}
            />
            <div className={styles.campaignBoxContent}>
              <h4>{heroSectionData.title}</h4>
              <p>{heroSectionData.description}</p>
              <a href={heroSectionData.campaignUrl}>{heroSectionData.campaignUrl}</a>
            </div>
          </div>
        )}

        <div className={styles.coverDetails}>
          {!heroSectionData.isCampaign && heroSectionData.typoGraphyImagePath && (
            <div className={styles.coverLogoImage}>
              <ImageGenerator
                noBlurPlaceholder={true}
                imageType="original"
                sizes={ResponsivePresets[ResponsivePresetKeys.BILLBOARD_TYPOGRAPHY]}
                src={heroSectionData.typoGraphyImagePath}
              />
            </div>
          )}

          {!heroSectionData.isCampaign && heroSectionData.title && (
            <div className={styles.titleContainer}>
              {heroSectionData.artist && (
                <IconButton
                  className={styles.goBackButton}
                  onEnterPress={() => {
                    history.goBack();
                  }}
                  onClick={() => {
                    history.goBack();
                  }}
                  focusKey="goBackButton"
                >
                  <IconComponent type="outlined" name="&#xe5e1;" />
                </IconButton>
              )}
              <h1 className={styles.heroSectionTitle}>{heroSectionData.title}</h1>
            </div>
          )}

          <div className={styles.staticDetailsAndLikes}>
            {likes > 0 && <div className={styles.likeStatics}>{likes} % پسندیدند</div>}
            {heroSectionData.productionYear && (
              <div className={styles.productionYear}>
                <span>تاریخ انتشار </span>
                <span>{heroSectionData.productionYear}</span>
              </div>
            )}
          </div>

          {heroSectionData.genres?.length! > 0 && (
            <div className={styles.heroSectionGenres}>
              {heroSectionData.genres?.slice(0, 5).map((item, index) => (
                <TagItem key={index} route={`/g/${item.descriptor}`} data={item} />
              ))}
            </div>
          )}

          {!heroSectionData.isCampaign && heroSectionData.description && (
            <p className={styles.descriptionContainer}>
              <span className={styles.description}>
                {heroSectionData.description.length > heroSectionData.descriptionMaxLength
                  ? heroSectionData.description.slice(0, heroSectionData.descriptionMaxLength) + "..."
                  : heroSectionData.description}
              </span>
              {heroSectionData.isShowMoreDescriptionButtonVisible &&
                heroSectionData.description.length > heroSectionData.descriptionMaxLength && (
                  <FocusableButton
                    focusKey="readMoreButton"
                    label="مشاهده بیشتر"
                    onClick={() => {
                      setShowDescriptionDialog(true);
                      setFocus("closeDialogButton");
                    }}
                  />
                )}
            </p>
          )}

          {heroSectionData.shouldInteractive && (
            <div className={styles.interactiveButtons}>
              {!heroSectionData.artist && (
                <div className={styles.buttonRow}>
                  <div className={clsx({ [styles.iranAccessButton]: heroSectionData.programStatusCode === 451 })}>
                    <FocusableButton
                      label={playButtonData?.label}
                      iconType="filled"
                      className={styles.interactionButton}
                      filled
                      focusKey="playButton"
                      startIcon={heroSectionData.programStatusCode === 451 ? "&#xf1ca;" : "&#xe037;"}
                      onClick={() => history.push(playButtonData.link)}
                    />
                  </div>

                  {!!LocalStorageService.get("tvrtk") && (
                    <div className={styles.buttonGroup}>
                      <FocusableButton
                        iconType={favorites?.interest === InterestAction.Like ? "filled" : "outlined"}
                        className={styles.interactionButton}
                        label=""
                        isCentered
                        focusKey="likeButton"
                        startIcon="&#xe8dc;"
                        onClick={() => onClickingInterestButton(InterestAction.Like)}
                      />
                      <FocusableButton
                        iconType={favorites?.interest === InterestAction.Dislike ? "filled" : "outlined"}
                        className={styles.interactionButton}
                        label=""
                        isCentered
                        focusKey="dislikeButton"
                        startIcon="&#xe8db;"
                        onClick={() => onClickingInterestButton(InterestAction.Dislike)}
                      />
                      <FocusableButton
                        iconType={favorites?.interest === InterestAction.Love ? "filled" : "outlined"}
                        className={styles.interactionButton}
                        label=""
                        isCentered
                        focusKey="loveButton"
                        startIcon="&#xe87d;"
                        onClick={() => onClickingInterestButton(InterestAction.Love)}
                      />
                      <FocusableButton
                        iconType={favorites?.isBookmarked ? "filled" : "outlined"}
                        className={styles.interactionButton}
                        label=""
                        isCentered
                        focusKey="bookmarkButton"
                        startIcon="&#xe866;"
                        onClick={onClickingBookmark}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {heroSectionData.episode?.accessType === EpisodeAccessType.OnlineScreening &&
            typeof osRemainingTime === "number" && (
              <span className={styles.onlineScreeningSpan}>
                {toReadableTimeFormat(osRemainingTime, true)} تا پایان مهلت مشاهده این فیلم باقی مانده است
              </span>
            )}
        </div>
      </div>
      {showDescriptionDialog &&
        heroSectionData.description &&
        heroSectionData.description.length > heroSectionData.descriptionMaxLength && (
          <TextDialog
            open={showDescriptionDialog}
            onClose={handleCloseDescriptionDialog}
            title={heroSectionData.title || "توضیحات"}
            description={heroSectionData.description}
          />
        )}
    </>
  );
};

export default HeroSection;
